import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, FileText } from "lucide-react";
import { opApi } from "@/lib/api";

const money = (n) => "$" + Number(n || 0).toFixed(2);
const ymd = (d) => d.toISOString().slice(0, 10);
const prettyMethod = (m) => ({ cash: "Cash", card: "Card", other: "Other" }[m] || (m || "Other"));

export function ZReportTab({ businessName = "Store" }) {
  const [day, setDay] = useState(ymd(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      const { data } = await opApi.get("/tenant/reports", { params: { date_from: d, date_to: d } });
      setData(data);
    } catch (e) { setData(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(day); }, [day, load]);

  const s = data?.summary || {};
  const payments = data?.payments || [];
  const channels = data?.channels || [];
  const cats = data?.categories || [];
  const net = Math.max(0, (s.gross || 0) - (s.discounts || 0));

  const line = (label, val, opts = {}) =>
    `<tr><td style="padding:3px 0;${opts.bold ? "font-weight:700;" : ""}${opts.indent ? "padding-left:14px;color:#555;" : ""}">${label}</td>` +
    `<td style="padding:3px 0;text-align:right;font-variant-numeric:tabular-nums;${opts.bold ? "font-weight:700;" : ""}">${val}</td></tr>`;

  const printZ = () => {
    const rows = [
      line("Gross sales", money(s.gross)),
      line("Discounts", "\u2212 " + money(s.discounts)),
      line("Net sales", money(net), { bold: true }),
      line("Tax", money(s.tax)),
      line("Total", money(s.total), { bold: true }),
      `<tr><td colspan="2"><hr style="border:none;border-top:1px dashed #999;margin:8px 0"/></td></tr>`,
      line("Orders", s.orders || 0),
      line("Average ticket", money(s.avg_ticket)),
      line("Collected", money(s.collected)),
      line("Outstanding", money(s.outstanding)),
      `<tr><td colspan="2"><hr style="border:none;border-top:1px dashed #999;margin:8px 0"/></td></tr>`,
      `<tr><td colspan="2" style="font-weight:700;padding-top:4px">Sales by payment</td></tr>`,
      ...payments.map((p) => line(prettyMethod(p.method), money(p.revenue), { indent: true })),
      `<tr><td colspan="2"><hr style="border:none;border-top:1px dashed #999;margin:8px 0"/></td></tr>`,
      `<tr><td colspan="2" style="font-weight:700;padding-top:4px">Sales by channel</td></tr>`,
      ...channels.map((c) => line((c.source || "other").toUpperCase(), money(c.revenue) + "  (" + c.orders + ")", { indent: true })),
    ].join("");
    const html = `<html><head><title>Z-Report ${day}</title></head><body style="font-family:ui-monospace,Menlo,monospace;max-width:360px;margin:24px auto;color:#111">
      <div style="text-align:center;margin-bottom:10px">
        <div style="font-weight:800;font-size:18px">${businessName}</div>
        <div style="font-size:13px;letter-spacing:2px;font-weight:700">Z-REPORT · DAILY CLOSE</div>
        <div style="font-size:12px;color:#555">${day}${data?.timezone ? " · " + data.timezone : ""}</div>
      </div>
      <table style="width:100%;font-size:13px;border-collapse:collapse">${rows}</table>
      <div style="text-align:center;font-size:11px;color:#888;margin-top:14px">Generated ${new Date().toLocaleString()}</div>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const w = window.open("", "_blank", "width=420,height=640");
    if (w) { w.document.write(html); w.document.close(); }
  };

  const Row = ({ label, value, bold, indent }) => (
    <div className={`flex items-center justify-between py-1.5 ${indent ? "pl-4" : ""} ${bold ? "font-bold" : ""}`}>
      <span className={indent ? "text-muted-foreground" : ""}>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="zreport-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-muted-foreground flex items-center gap-1.5"><FileText className="h-4 w-4" /> Daily register close</div>
        <div className="flex items-center gap-2 ml-auto">
          <input type="date" className="h-9 rounded-none border border-input bg-background px-2 text-sm" value={day} onChange={(e) => setDay(e.target.value)} data-testid="zreport-date" />
          <Button variant="outline" className="rounded-none h-9 gap-2" onClick={printZ} disabled={!data} data-testid="zreport-print"><Printer className="h-4 w-4" /> Print Z-Report</Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="max-w-md mx-auto border border-border bg-card p-6" data-testid="zreport-card">
          <div className="text-center mb-4 pb-4 border-b border-dashed border-border">
            <div className="font-display font-extrabold text-lg">{businessName}</div>
            <div className="text-xs font-bold tracking-[0.2em] text-muted-foreground">Z-REPORT · DAILY CLOSE</div>
            <div className="text-xs text-muted-foreground mt-0.5">{day}{data?.timezone ? ` · ${data.timezone}` : ""}</div>
          </div>
          <div className="text-sm">
            <Row label="Gross sales" value={money(s.gross)} />
            <Row label="Discounts" value={"\u2212 " + money(s.discounts)} />
            <Row label="Net sales" value={money(net)} bold />
            <Row label="Tax" value={money(s.tax)} />
            <Row label="Total" value={money(s.total)} bold />
            <div className="border-t border-dashed border-border my-2" />
            <Row label="Orders" value={s.orders || 0} />
            <Row label="Average ticket" value={money(s.avg_ticket)} />
            <Row label="Collected" value={money(s.collected)} />
            <Row label="Outstanding" value={money(s.outstanding)} />
            <div className="border-t border-dashed border-border my-2" />
            <div className="font-bold pt-1 pb-0.5">Sales by payment</div>
            {payments.length ? payments.map((p) => <Row key={p.method} label={prettyMethod(p.method)} value={money(p.revenue)} indent />) : <div className="pl-4 text-muted-foreground text-xs py-1">No sales.</div>}
            <div className="border-t border-dashed border-border my-2" />
            <div className="font-bold pt-1 pb-0.5">Sales by channel</div>
            {channels.length ? channels.map((c) => <Row key={c.source} label={(c.source || "other").toUpperCase()} value={`${money(c.revenue)} · ${c.orders}`} indent />) : <div className="pl-4 text-muted-foreground text-xs py-1">No sales.</div>}
            {cats.length > 0 && (<>
              <div className="border-t border-dashed border-border my-2" />
              <div className="font-bold pt-1 pb-0.5">Sales by category</div>
              {cats.map((c) => <Row key={c.name} label={c.name} value={money(c.revenue)} indent />)}
            </>)}
          </div>
          <div className="text-center text-[11px] text-muted-foreground mt-4 pt-3 border-t border-dashed border-border">Generated {new Date().toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}
